import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Collapse,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore, OpenInNew, Refresh } from '@mui/icons-material';
import { toast } from 'react-toastify';
import BillingNav from './BillingNav';
import ProductCostGrid from './ProductCostGrid';
import {
  BillingPage,
  BillingPanel,
  StatusChip,
  btnGhost,
  billingSx,
} from './billingUi';
import {
  changePricingPlanMidTerm,
  fetchPricingPlans,
  fetchTenantCharges,
  fetchTenantProductGrid,
  fetchTenantSubscriptions,
  formatMoney,
  previewPricingPlanChange,
  upgradeSubscriptionCharge,
} from '../../services/billingApi';
import { fetchTenants } from '../../services/licensingApi';

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const baseChargeCode = (code) => {
  if (!code) return '';
  const idx = String(code).indexOf('_UPG_');
  return idx > 0 ? String(code).substring(0, idx) : String(code);
};

/** Mid-cycle qty add-ons only — not primary rows after a plan change. */
const isUpgradeCharge = (c) =>
  String(c?.chargeCode || '').includes('_UPG_')
  || /—\s*upgrade\s*\(\+/i.test(String(c?.chargeName || ''));

/** Group parent + mid-cycle upgrade charges into one effective row per resource. */
function buildEffectiveLimits(charges) {
  const parents = charges.filter((c) => !isUpgradeCharge(c));
  const upgrades = charges.filter((c) => isUpgradeCharge(c));
  const used = new Set();

  const byParentId = new Map();
  for (const u of upgrades) {
    if (u.parentSubscriptionChargeId == null) continue;
    const key = String(u.parentSubscriptionChargeId);
    if (!byParentId.has(key)) byParentId.set(key, []);
    byParentId.get(key).push(u);
  }

  const rows = parents.map((p) => {
    const children = [...(byParentId.get(String(p.subscriptionChargeId)) || [])];
    children.forEach((c) => used.add(String(c.subscriptionChargeId)));

    const base = baseChargeCode(p.chargeCode);
    for (const u of upgrades) {
      if (used.has(String(u.subscriptionChargeId))) continue;
      if (baseChargeCode(u.chargeCode) !== base) continue;
      if (String(u.subscriptionId || '') !== String(p.subscriptionId || '')) continue;
      children.push(u);
      used.add(String(u.subscriptionChargeId));
    }

    children.sort((a, b) => toNum(a.subscriptionChargeId) - toNum(b.subscriptionChargeId));
    const originalQty = toNum(p.contractedQuantity);
    const addedQty = children.reduce((sum, c) => sum + toNum(c.contractedQuantity), 0);
    const unitPrice = toNum(p.contractedUnitPrice);
    const effectiveQty = originalQty + addedQty;

    return {
      key: String(p.subscriptionChargeId),
      parent: p,
      upgrades: children,
      name: p.chargeName || p.chargeCode || 'Charge',
      productCode: p.productCode || '—',
      unit: p.unit || '',
      originalQty,
      addedQty,
      effectiveQty,
      unitPrice,
      lineMonthly: effectiveQty * unitPrice,
    };
  });

  // Orphan upgrades (parent inactive/missing) — show as their own effective rows
  for (const u of upgrades) {
    if (used.has(String(u.subscriptionChargeId))) continue;
    const qty = toNum(u.contractedQuantity);
    const unitPrice = toNum(u.contractedUnitPrice);
    rows.push({
      key: `orphan-${u.subscriptionChargeId}`,
      parent: u,
      upgrades: [],
      name: (u.chargeName || u.chargeCode || 'Upgrade').replace(/\s*—\s*upgrade\s*\([^)]*\)/i, '').trim() || 'Charge',
      productCode: u.productCode || '—',
      unit: u.unit || '',
      originalQty: 0,
      addedQty: qty,
      effectiveQty: qty,
      unitPrice,
      lineMonthly: qty * unitPrice,
      orphan: true,
    });
  }

  return rows;
}

const planPreviewTotals = (preview) => {
  const lines = Array.isArray(preview?.lines) ? preview.lines : [];
  let currentMonthly = 0;
  let newMonthly = 0;
  for (const l of lines) {
    const qty = toNum(l.quantity);
    currentMonthly += qty * toNum(l.oldUnitPrice);
    newMonthly += qty * toNum(l.newUnitPrice);
  }
  return {
    currentMonthly,
    newMonthly,
    netDelta: toNum(preview?.netDelta),
    remainingMonths: toNum(preview?.remainingMonths),
  };
};

const BillingSubscriptions = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tenantId, setTenantId] = useState(searchParams.get('tenantId') || '');
  const [tenants, setTenants] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [grid, setGrid] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [charges, setCharges] = useState([]);
  const [extraOpen, setExtraOpen] = useState(false);
  const [extraChargeId, setExtraChargeId] = useState('');
  const [extraQty, setExtraQty] = useState('1');
  const [extraSaving, setExtraSaving] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState('');
  const [planPreview, setPlanPreview] = useState(null);
  const [planSaving, setPlanSaving] = useState(false);
  const [selectedSubId, setSelectedSubId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const currency = 'USD';

  const loadTenants = useCallback(async () => {
    try {
      setTenants(await fetchTenants());
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const loadTenant = useCallback(async () => {
    if (!tenantId) {
      setSubscription(null);
      setGrid(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const subs = await fetchTenantSubscriptions(tenantId);
      setSubscriptions(subs);
      const keyOf = (s) => (s?.source === 'BILLING' ? `B-${s.billingSubscriptionId}` : `L-${s?.subscriptionId}`);
      const picked = subs.find((s) => keyOf(s) === String(selectedSubId)) || subs[0] || null;
      setSubscription(picked);
      if (picked && keyOf(picked) !== String(selectedSubId)) {
        setSelectedSubId(keyOf(picked));
      }
      setCharges(await fetchTenantCharges(tenantId));
      const g = await fetchTenantProductGrid(tenantId, picked?.subscriptionId || undefined);
      setGrid(g);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId, selectedSubId]);

  useEffect(() => {
    loadTenant();
  }, [loadTenant]);

  const visibleCharges = useMemo(
    () =>
      charges.filter((c) => c.active !== false && (
        subscription?.source === 'BILLING'
          ? String(c.billingSubscriptionId) === String(subscription.billingSubscriptionId)
          : String(c.subscriptionId) === String(subscription?.subscriptionId)
      )),
    [charges, subscription]
  );

  const effectiveLimits = useMemo(() => buildEffectiveLimits(visibleCharges), [visibleCharges]);
  const increasableLimits = useMemo(
    () => effectiveLimits.filter((r) => !r.orphan && r.parent?.subscriptionChargeId != null),
    [effectiveLimits]
  );
  const monthlyContractedTotal = useMemo(
    () => effectiveLimits.reduce((sum, r) => sum + r.lineMonthly, 0),
    [effectiveLimits]
  );

  const selectedIncrease = increasableLimits.find((r) => String(r.parent.subscriptionChargeId) === String(extraChargeId));
  const addQtyNum = Number(extraQty);
  const previewNewQty =
    selectedIncrease && Number.isFinite(addQtyNum) && addQtyNum > 0
      ? selectedIncrease.effectiveQty + addQtyNum
      : null;
  const previewBillAmount =
    selectedIncrease && Number.isFinite(addQtyNum) && addQtyNum > 0
      ? addQtyNum * selectedIncrease.unitPrice
      : null;

  const planTotals = planPreview ? planPreviewTotals(planPreview) : null;
  const selectedPlan = plans.find((p) => String(p.pricingPlanId) === String(planId)) || null;
  const selectedPlanRates = useMemo(() => {
    if (!selectedPlan) return [];
    const items = Array.isArray(selectedPlan.items) ? selectedPlan.items : [];
    const qtyByCode = new Map();
    for (const l of planPreview?.lines || []) {
      qtyByCode.set(String(l.chargeCode), toNum(l.quantity));
    }
    return items
      .filter((it) => it.active !== false)
      .map((it) => {
        const rate = toNum(it.monthlyUnitPrice != null ? it.monthlyUnitPrice : it.unitPrice);
        const code = String(it.chargeCode || '');
        const qty = qtyByCode.has(code) ? qtyByCode.get(code) : (toNum(it.includedQuantity) || 1);
        return {
          key: String(it.pricingItemId || code),
          name: it.chargeName || code,
          code,
          productCode: it.productCode || '—',
          unit: it.unit || '',
          rate,
          qty,
          monthly: rate * qty,
        };
      })
      .filter((r) => r.rate > 0)
      .sort((a, b) => b.rate - a.rate || a.name.localeCompare(b.name));
  }, [selectedPlan, planPreview]);

  const toggleExpanded = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openIncreaseQuantity = (preferChargeId) => {
    const first = preferChargeId
      || (increasableLimits[0] ? String(increasableLimits[0].parent.subscriptionChargeId) : '');
    setExtraChargeId(first);
    setExtraQty('1');
    setExtraOpen(true);
  };

  const addExtraLimit = async () => {
    const row = increasableLimits.find((r) => String(r.parent.subscriptionChargeId) === String(extraChargeId));
    const add = Number(extraQty);
    if (!row || !Number.isFinite(add) || add <= 0) {
      toast.error('Choose a resource and enter how much to add');
      return;
    }
    setExtraSaving(true);
    try {
      const result = await upgradeSubscriptionCharge(row.parent.subscriptionChargeId, add);
      const invNo = result?.invoice?.invoiceNumber || result?.invoice?.invoice_number;
      const from = row.effectiveQty;
      const to = from + add;
      toast.success(
        invNo
          ? `${row.name}: ${from} → ${to}. Supplemental invoice ${invNo} for +${add}.`
          : `${row.name}: ${from} → ${to}. Supplemental invoice created for +${add}.`
      );
      setExtraOpen(false);
      setExtraQty('1');
      await loadTenant();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExtraSaving(false);
    }
  };

  const openPlanChange = async () => {
    if (!subscription?.subscriptionId) {
      toast.error('Select a license subscription');
      return;
    }
    setPlanOpen(true);
    setPlanPreview(null);
    setPlanId('');
    try {
      const list = await fetchPricingPlans(false);
      setPlans(Array.isArray(list) ? list.filter((p) => p.active !== false) : []);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const runPlanPreview = async (id) => {
    setPlanId(id);
    setPlanPreview(null);
    if (!id || !subscription?.subscriptionId) return;
    try {
      setPlanPreview(await previewPricingPlanChange(subscription.subscriptionId, id));
    } catch (e) {
      toast.error(e.message);
    }
  };

  const applyPlanChange = async () => {
    if (!subscription?.subscriptionId || !planId) {
      toast.error('Choose a pricing plan');
      return;
    }
    setPlanSaving(true);
    try {
      const result = await changePricingPlanMidTerm(subscription.subscriptionId, planId);
      const invNo = result?.invoice?.invoiceNumber;
      const creditAmt = result?.credit?.amount;
      if (result?.direction === 'UPGRADE' && invNo) {
        toast.success(`Plan upgraded. Supplemental invoice ${invNo} for remaining term.`);
      } else if (result?.direction === 'DOWNGRADE' && creditAmt != null) {
        toast.success(`Plan downgraded. Credit ${creditAmt} for unused prepaid months.`);
      } else {
        toast.success('Plan updated for the remaining term.');
      }
      setPlanOpen(false);
      await loadTenant();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPlanSaving(false);
    }
  };

  return (
    <BillingPage>
      <BillingNav
        title="Subscriptions"
        subtitle="Contracted quantities and prices for this tenant. Increasing quantity bills only the added amount on a supplemental invoice."
        actions={
          tenantId ? (
            <Button
              size="small"
              startIcon={<OpenInNew />}
              sx={btnGhost}
              variant="outlined"
              onClick={() => navigate(`/admin/license?tenantId=${tenantId}`)}
            >
              Open license
            </Button>
          ) : null
        }
      />

      <BillingPanel padded sx={{ mb: 2 }} tone="teal">
        <Box sx={{ ...billingSx.toolbar, mb: 0 }}>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Tenant</InputLabel>
            <Select
              label="Tenant"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
            >
              <MenuItem value="">Select tenant</MenuItem>
              {tenants.map((t) => (
                <MenuItem
                  key={t.tenantId || t.tenant_id}
                  value={String(t.tenantId || t.tenant_id)}
                >
                  {t.tenantName || t.tenant_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {subscription && (
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              sx={btnGhost}
              onClick={loadTenant}
            >
              Refresh
            </Button>
          )}
        </Box>
        {subscriptions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 280, mt: 1 }}>
            <InputLabel>Subscription</InputLabel>
            <Select
              label="Subscription"
              value={selectedSubId}
              onChange={(e) => setSelectedSubId(String(e.target.value))}
            >
              {subscriptions.map((s) => {
                const key = s.source === 'BILLING' ? `B-${s.billingSubscriptionId}` : `L-${s.subscriptionId}`;
                return (
                  <MenuItem key={key} value={key}>
                    {s.source === 'BILLING' ? 'Billing' : 'License'} #{s.billingSubscriptionId || s.subscriptionId}{' '}
                    {s.planName || s.planCode || ''} {s.subscriptionStatus}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}
        {subscription && (
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              License #{subscription.subscriptionId}
            </Typography>
            <StatusChip status={subscription.subscriptionStatus} />
            <Button
              size="small"
              variant="outlined"
              onClick={() => openIncreaseQuantity()}
              disabled={increasableLimits.length === 0}
            >
              Increase quantity
            </Button>
            {subscription?.source !== 'BILLING' && (
              <Button size="small" variant="outlined" onClick={openPlanChange}>
                Change plan
              </Button>
            )}
            <Typography variant="body2" color="text.secondary">
              {subscription.planName || subscription.planCode} · {subscription.billingCycle}
            </Typography>
          </Box>
        )}
      </BillingPanel>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !tenantId ? (
        <BillingPanel padded>
          <Typography color="text.secondary">Select a tenant to view the product cost grid.</Typography>
        </BillingPanel>
      ) : subscription?.source === 'BILLING' ? (
        <BillingPanel padded>
          <Typography color="text.secondary">
            Billing subscription charges are listed below. License products stay on the license subscription.
          </Typography>
        </BillingPanel>
      ) : (
        <>
          <ProductCostGrid grid={grid} dense />
          <Alert severity="info" sx={{ mt: 1.5, mb: 0 }}>
            <strong>Product grid</strong> = what you are billed for each enabled product (qty × unit price).{' '}
            <strong>Quantities &amp; pricing</strong> below = the billing contract lines behind those costs
            (original qty, mid-cycle adds, effective total). Increasing quantity updates both after refresh;
            Change plan keeps your effective quantities and only switches unit rates.
          </Alert>
        </>
      )}

      {subscription && effectiveLimits.length > 0 && (
        <BillingPanel padded sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 1, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="subtitle1">Quantities &amp; pricing</Typography>
              <Typography variant="body2" color="text.secondary">
                Billing contract for this subscription. Mid-cycle increases keep the original line and bill only the added quantity.
              </Typography>
            </Box>
            <Typography variant="subtitle2">
              Monthly contracted: {formatMoney(monthlyContractedTotal, currency)}
            </Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>Resource</TableCell>
                <TableCell>Product</TableCell>
                <TableCell align="right">Original</TableCell>
                <TableCell align="right">Added</TableCell>
                <TableCell align="right">Effective qty</TableCell>
                <TableCell align="right">Unit price</TableCell>
                <TableCell align="right">Monthly</TableCell>
                <TableCell align="right" width={120} />
              </TableRow>
            </TableHead>
            <TableBody>
              {effectiveLimits.map((row) => {
                const hasHistory = row.upgrades.length > 0;
                const open = expandedKeys.has(row.key);
                return (
                  <React.Fragment key={row.key}>
                    <TableRow hover>
                      <TableCell sx={{ py: 0.5 }}>
                        {hasHistory ? (
                          <IconButton size="small" onClick={() => toggleExpanded(row.key)} aria-label="Show history">
                            {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                          </IconButton>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.name}</Typography>
                        {row.unit ? (
                          <Typography variant="caption" color="text.secondary">
                            per {row.unit}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{row.productCode}</TableCell>
                      <TableCell align="right">{row.originalQty}</TableCell>
                      <TableCell align="right">
                        {row.addedQty > 0 ? `+${row.addedQty}` : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>{row.effectiveQty}</Typography>
                      </TableCell>
                      <TableCell align="right">{formatMoney(row.unitPrice, currency)}</TableCell>
                      <TableCell align="right">{formatMoney(row.lineMonthly, currency)}</TableCell>
                      <TableCell align="right">
                        {!row.orphan && (
                          <Button
                            size="small"
                            onClick={() => openIncreaseQuantity(String(row.parent.subscriptionChargeId))}
                          >
                            Increase
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {hasHistory && (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                                Billing history (original contract kept; upgrades billed separately)
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Record</TableCell>
                                    <TableCell align="right">Qty</TableCell>
                                    <TableCell align="right">Unit price</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  <TableRow>
                                    <TableCell>Original contract</TableCell>
                                    <TableCell align="right">{row.originalQty}</TableCell>
                                    <TableCell align="right">{formatMoney(row.unitPrice, currency)}</TableCell>
                                  </TableRow>
                                  {row.upgrades.map((u) => (
                                    <TableRow key={u.subscriptionChargeId}>
                                      <TableCell>
                                        Mid-cycle increase (+{toNum(u.contractedQuantity)})
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                          Charge #{u.subscriptionChargeId}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">{toNum(u.contractedQuantity)}</TableCell>
                                      <TableCell align="right">
                                        {formatMoney(u.contractedUnitPrice, currency)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
              <TableRow>
                <TableCell colSpan={7} align="right">
                  <Typography variant="subtitle2">Monthly contracted total</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle2">{formatMoney(monthlyContractedTotal, currency)}</Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </BillingPanel>
      )}

      <Dialog open={extraOpen} onClose={() => !extraSaving && setExtraOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Increase quantity</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Like a typical SaaS upgrade: you keep the original contracted quantity, and we invoice only
            for the amount you add.
          </Typography>
          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Resource</InputLabel>
            <Select
              label="Resource"
              value={extraChargeId}
              onChange={(e) => setExtraChargeId(String(e.target.value))}
            >
              {increasableLimits.map((r) => (
                <MenuItem key={r.key} value={String(r.parent.subscriptionChargeId)}>
                  {r.name} · current {r.effectiveQty} · {formatMoney(r.unitPrice, currency)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="dense"
            label="Quantity to add"
            type="number"
            inputProps={{ min: 1, step: 1 }}
            value={extraQty}
            onChange={(e) => setExtraQty(e.target.value)}
            helperText={
              selectedIncrease && previewNewQty != null
                ? `${selectedIncrease.name}: ${selectedIncrease.effectiveQty} → ${previewNewQty}. Bill ${formatMoney(previewBillAmount, currency)} for +${addQtyNum}.`
                : 'Enter how many to add (not the new total).'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtraOpen(false)} disabled={extraSaving}>Cancel</Button>
          <Button variant="contained" onClick={addExtraLimit} disabled={extraSaving || !extraChargeId}>
            {extraSaving ? 'Billing…' : 'Confirm & invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={planOpen} onClose={() => !planSaving && setPlanOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Change plan</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Switch rates for the rest of the term. We compare your current monthly cost to the new plan
            at the same quantities, then bill or credit the difference for remaining months.
          </Typography>
          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>New pricing plan</InputLabel>
            <Select
              label="New pricing plan"
              value={planId}
              onChange={(e) => runPlanPreview(String(e.target.value))}
            >
              <MenuItem value="">Select plan</MenuItem>
              {plans.map((p) => {
                const users = (p.items || []).find((it) =>
                  /licensed.?user|MAX_USERS|LICENSED_USERS/i.test(
                    `${it.chargeCode || ''} ${it.chargeName || ''}`
                  )
                );
                const userRate = users
                  ? toNum(users.monthlyUnitPrice != null ? users.monthlyUnitPrice : users.unitPrice)
                  : null;
                return (
                  <MenuItem key={p.pricingPlanId} value={String(p.pricingPlanId)}>
                    {p.planName || p.planCode} · {p.billingFrequency || ''}
                    {userRate != null ? ` · users ${formatMoney(userRate, currency)}` : ''}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {selectedPlan && selectedPlanRates.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {selectedPlan.planName || selectedPlan.planCode} — rates on this plan
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Priced lines on this plan at your current quantities. $0 catalog lines are hidden.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Resource</TableCell>
                    <TableCell align="right">Unit rate</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Monthly</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedPlanRates.map((r) => (
                    <TableRow key={r.key}>
                      <TableCell>
                        {r.name}
                        {r.unit ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            per {r.unit}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">{formatMoney(r.rate, currency)}</TableCell>
                      <TableCell align="right">{r.qty}</TableCell>
                      <TableCell align="right">{formatMoney(r.monthly, currency)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {planPreview && planTotals && (
            <Box sx={{ mt: 2.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                What happens if you switch (remaining ~{Math.round(planTotals.remainingMonths * 10) / 10} months)
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                  gap: 1.5,
                }}
              >
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Current monthly</Typography>
                  <Typography variant="subtitle1">{formatMoney(planTotals.currentMonthly, currency)}</Typography>
                </Box>
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">New plan monthly</Typography>
                  <Typography variant="subtitle1">{formatMoney(planTotals.newMonthly, currency)}</Typography>
                </Box>
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {planPreview.direction === 'DOWNGRADE'
                      ? 'Credit for remaining term'
                      : planPreview.direction === 'UPGRADE'
                        ? 'Charge for remaining term'
                        : 'Adjustment'}
                  </Typography>
                  <Typography variant="subtitle1">
                    {planPreview.direction === 'DOWNGRADE' && formatMoney(Math.abs(planTotals.netDelta), currency)}
                    {planPreview.direction === 'UPGRADE' && formatMoney(planTotals.netDelta, currency)}
                    {planPreview.direction === 'NO_CHANGE' && 'No change'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanOpen(false)} disabled={planSaving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={applyPlanChange}
            disabled={planSaving || !planId || planPreview?.direction === 'NO_CHANGE'}
          >
            {planSaving ? 'Applying…' : 'Apply plan change'}
          </Button>
        </DialogActions>
      </Dialog>
    </BillingPage>
  );
};

export default BillingSubscriptions;
